// ======================================================
// FILE: src/pages/Home/Home.jsx
// FULL PREMIUM HOME PAGE
// COMPLETE FIXED FILE
// NOTHING SKIPPED
// ======================================================

import React, { useEffect, useState } from "react";

import {
  FiSearch,
  FiHeart,
  FiArrowRight,
  FiClock,
  FiStar,
  FiMinus,
  FiPlus,
} from "react-icons/fi";

import { Link, useNavigate } from "react-router-dom";

import Slider from "react-slick";

import { MagnifyingGlass } from "react-loader-spinner";

import { Slide, Zoom } from "react-awesome-reveal";

import api from "../utils/api";

import "./Home.css";

/* ======================================================
   IMPORTS
====================================================== */

// HERO
import slider1 from "../images/slide-1.jpg";
import slider2 from "../images/slider-2.jpg";

// ABOUT
import abouticon from "../images/about-icons-1.svg";

// PROMO BANNERS
import adbanner1 from "../images/ad-banner-1.jpg";
import adbanner2 from "../images/ad-banner-2.jpg";
import adbanner3 from "../images/ad-banner-3.jpg";

// CATEGORY IMAGES
import attaricedal from "../images/atta-rice-dal.png";
import petcare from "../images/pet-care.png";
import cleaningessentials from "../images/cleaning-essentials.png";
import babycare from "../images/baby-care.png";
import chickenmeatfish from "../images/chicken-meat-fish.png";
import colddrinksjuices from "../images/cold-drinks-juices.png";
import teacoffeedrinks from "../images/tea-coffee-drinks.png";
import instantfood from "../images/instant-food.png";
import bakerybiscuits from "../images/bakery-biscuits.png";
import snackmunchies from "../images/snack-munchies.png";
import fruitsvegetables from "../images/fruits-vegetables.png";
import dairybreadeggs from "../images/dairy-bread-eggs.png";

// GROCERY BANNERS
import grocerybanner from "../images/grocery-banner.png";
import grocerybanner2 from "../images/grocery-banner-2.jpg";

// APP DOWNLOAD
import map from "../images/map.png";
import iphone from "../images/iphone-2.png";
import googleplay from "../images/googleplay-btn.svg";
import appstore from "../images/appstore-btn.svg";

// FEATURES
import clock from "../images/clock.svg";
import gift from "../images/gift.svg";
import package1 from "../images/package.svg";
import refresh from "../images/refresh-cw.svg";

// BOTTOM CATEGORY SLIDER
import product1 from "../images/category-baby-care.jpg";
import product2 from "../images/category-atta-rice-dal.jpg";
import product3 from "../images/category-bakery-biscuits.jpg";
import product4 from "../images/category-chicken-meat-fish.jpg";
import product5 from "../images/category-cleaning-essentials.jpg";
import product6 from "../images/category-dairy-bread-eggs.jpg";
import product7 from "../images/category-instant-food.jpg";
import product8 from "../images/category-pet-care.jpg";
import product9 from "../images/category-snack-munchies.jpg";
import product10 from "../images/category-tea-coffee-drinks.jpg";

const BASE_URL = "https://api.freshcart4u.com";

/* ======================================================
   HERO SLIDER SETTINGS
====================================================== */

const heroSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  arrows: false,
};

/* ======================================================
   BOTTOM CATEGORY SLIDER
====================================================== */

const categorySliderSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 5,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2200,
  arrows: false,

  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 4,
      },
    },

    {
      breakpoint: 768,
      settings: {
        slidesToShow: 3,
      },
    },

    {
      breakpoint: 480,
      settings: {
        slidesToShow: 2,
      },
    },
  ],
};

function Home() {
  const navigate = useNavigate();

  /* ======================================================
      STATES
  ====================================================== */

  const [loader, setLoader] = useState(true);

  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState("");

  const [cart, setCart] = useState({});

  const [isVisible, setIsVisible] = useState(false);

  const userId = localStorage.getItem("userId");

  const token = localStorage.getItem("token");

  /* ======================================================
      LOADER
  ====================================================== */

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  /* ======================================================
      SCROLL TOP BUTTON
  ====================================================== */

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);

    return () =>
      window.removeEventListener(
        "scroll",
        toggleVisibility
      );
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ======================================================
      FETCH PRODUCTS
  ====================================================== */

  const fetchProducts = async () => {
    try {
      const res = await api.get("/api/products", {
        params: {
          page: 1,
          limit: 300,
        },
      });

      const list = res.data.products || [];

      setProducts(list);

      const uniqueCategories = [
        ...new Set(
          list
            .map((p) => p.category)
            .filter(Boolean)
        ),
      ];

      setCategories(uniqueCategories);
    } catch (err) {
      console.log(err);
    }
  };

  /* ======================================================
      FETCH CART
  ====================================================== */

  const fetchCart = async () => {
    if (!token || !userId) return;

    try {
      const res = await api.get(`/api/cart/${userId}`);

      const items = res.data.items || [];

      const cartMap = {};

      items.forEach((item) => {
        const product =
          item.productId || item.product;

        if (product) {
          cartMap[product._id] = item.quantity;
        }
      });

      setCart(cartMap);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCart();
  }, []);

  /* ======================================================
      CART AUTO REFRESH
  ====================================================== */

  useEffect(() => {
    const handleCart = () => {
      fetchCart();
    };

    window.addEventListener(
      "cartUpdated",
      handleCart
    );

    return () => {
      window.removeEventListener(
        "cartUpdated",
        handleCart
      );
    };
  }, []);

  /* ======================================================
      GET PRODUCT IMAGE
  ====================================================== */

  const getImage = (img) => {
    if (!img) {
      return "https://via.placeholder.com/300";
    }

    if (img.startsWith("http")) {
      return img;
    }

    return `${BASE_URL}${img}`;
  };

  /* ======================================================
      CALCULATE DISCOUNT
  ====================================================== */

  const getDiscount = (mrp, price) => {
    if (!mrp || mrp <= price) {
      return 0;
    }

    return Math.round(((mrp - price) / mrp) * 100);
  };

  /* ======================================================
      SEARCH
  ====================================================== */

  const handleSearch = () => {
    if (!search.trim()) return;

    navigate(`/shop?search=${search}`);
  };

  /* ======================================================
      UPDATE CART
  ====================================================== */

  const updateQty = async (productId, change) => {
    if (!token || !userId) {
      navigate("/MyAccountSignIn");
      return;
    }

    const currentQty = cart[productId] || 0;

    const newQty = currentQty + change;

    /* REMOVE */

    if (newQty <= 0) {
      try {
        await api.post("/api/cart/remove", {
          userId,
          productId,
        });

        setCart((prev) => {
          const updated = { ...prev };

          delete updated[productId];

          return updated;
        });

        window.dispatchEvent(
          new Event("cartUpdated")
        );
      } catch (err) {
        console.log(err);
      }

      return;
    }

    /* UPDATE UI */

    setCart((prev) => ({
      ...prev,
      [productId]: newQty,
    }));

    try {
      /* ADD */

      if (currentQty === 0) {
        await api.post("/api/cart/add", {
          userId,
          productId,
          quantity: 1,
        });
      }

      /* UPDATE */

      else {
        await api.post("/api/cart/update", {
          userId,
          productId,
          quantity: newQty,
        });
      }

      window.dispatchEvent(
        new Event("cartUpdated")
      );
    } catch (err) {
      console.log(err);
    }
  };

  /* ======================================================
      BEST SELLING
  ====================================================== */

  const bestSelling = [...products]
    .sort((a, b) => (b.price || 0) - (a.price || 0))
    .slice(0, 8);

  /* ======================================================
      HOT DEALS
  ====================================================== */

  const hotDeals = [...products]
    .sort(
      (a, b) =>
        getDiscount(b.mrp, b.price) -
        getDiscount(a.mrp, a.price)
    )
    .slice(0, 8);

  /* ======================================================
      TOTALS
  ====================================================== */

  const totalItems = Object.values(cart).reduce(
    (a, b) => a + b,
    0
  );

  const totalPrice = products.reduce((total, p) => {
    const qty = cart[p._id] || 0;

    return total + qty * p.price;
  }, 0);

  /* ======================================================
      PRODUCT CARD
  ====================================================== */

  const ProductCard = ({ p }) => {
    const discount = getDiscount(
      p.mrp,
      p.price
    );

    return (
      <div className="product-card">
        {discount > 0 && (
          <div className="discount-tag">
            {discount}% OFF
          </div>
        )}

        <button className="wishlist-btn">
          <FiHeart />
        </button>

        <div
          className="product-image clickable-product"
          onClick={() =>
            navigate(`/product/${p._id}`)
          }
        >
          <img
            src={getImage(p.images?.[0])}
            alt={p.name}
          />
        </div>

        <div className="product-top-tags">
          <div className="delivery-mini">
            <FiClock />
            10 MIN
          </div>

          <div className="rating-box">
            <FiStar />
            4.5
          </div>
        </div>

        <span className="product-category">
          {p.category}
        </span>

        <h3
          className="clickable-product"
          onClick={() =>
            navigate(`/product/${p._id}`)
          }
        >
          {p.name}
        </h3>

        <div className="price-row">
          <div>
            {p.mrp > p.price && (
              <span className="old-price">
                ₹{p.mrp}
              </span>
            )}

            <h2>₹{p.price}</h2>
          </div>

          {cart[p._id] ? (
            <div className="qty-control">
              <button
                onClick={() =>
                  updateQty(p._id, -1)
                }
              >
                <FiMinus />
              </button>

              <span>{cart[p._id]}</span>

              <button
                onClick={() =>
                  updateQty(p._id, 1)
                }
              >
                <FiPlus />
              </button>
            </div>
          ) : (
            <button
              className="add-btn"
              onClick={() =>
                updateQty(p._id, 1)
              }
            >
              ADD
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ======================================================
      RETURN
  ====================================================== */

  return (
    <div className="home-container">
      {loader ? (
        <div className="loader-wrapper">
          <MagnifyingGlass
            visible={true}
            height="100"
            width="100"
            color="#0aad0a"
          />
        </div>
      ) : (
        <>
          {/* ======================================================
              SCROLL TO TOP
          ====================================================== */}

          <div className="scroll-to-top">
            <button
              onClick={scrollToTop}
              className={`scroll-to-top-button ${
                isVisible ? "show" : ""
              }`}
            >
              ↑
            </button>
          </div>

          {/* ======================================================
              SEARCH
          ====================================================== */}

          <div className="search-wrapper">
            <FiSearch />

            <input
              type="text"
              placeholder='Search "Milk", "Eggs"...'
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </div>

          {/* ======================================================
              HERO SLIDER
          ====================================================== */}

          <section className="hero-section">
            <div className="banner-wrapper">
              <Slider {...heroSettings}>
                {/* SLIDE 1 */}

                <div>
                  <div
                    className="hero-slide"
                    style={{
                      background: `url(${slider1})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <div className="hero-content">
                      <span className="hero-badge">
                        Opening Sale Discount 50%
                      </span>

                      <h2>
                        SuperMarket Daily
                        <br />
                        Fresh Grocery
                      </h2>

                      <p>
                        Fresh groceries delivered
                        instantly to your home.
                      </p>

                      <Link
                        to="/shop"
                        className="hero-btn"
                      >
                        Shop Now
                      </Link>
                    </div>
                  </div>
                </div>

                {/* SLIDE 2 */}

                <div>
                  <div
                    className="hero-slide"
                    style={{
                      background: `url(${slider2})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <div className="hero-content">
                      <span className="hero-badge">
                        Free Shipping
                      </span>

                      <h2>
                        Free Shipping On
                        <br />
                        Orders Over ₹200
                      </h2>

                      <p>
                        Fast grocery delivery with
                        amazing discounts.
                      </p>

                      <Link
                        to="/shop"
                        className="hero-btn"
                      >
                        Shop Now
                      </Link>
                    </div>
                  </div>
                </div>
              </Slider>
            </div>
          </section>

          {/* ======================================================
              ABOUT SECTION
          ====================================================== */}

        

          {/* ======================================================
              PROMO BANNERS
          ====================================================== */}

          <section className="promo-section">
            <div className="promo-grid">
              {/* 1 */}

              <Slide direction="left">
                <div className="promo-banner">
                  <img
                    src={adbanner1}
                    alt=""
                  />

                  <div className="promo-content">
                    <h3>
                      Personal Care
                    </h3>

                    <p>
                      Cashback Offers
                    </p>
                  </div>
                </div>
              </Slide>

              {/* 2 */}

              <Zoom>
                <div className="promo-banner">
                  <img
                    src={adbanner2}
                    alt=""
                  />

                  <div className="promo-content">
                    <h3>
                      Fresh Fruits
                    </h3>

                    <p>
                      Healthy Everyday
                    </p>
                  </div>
                </div>
              </Zoom>

              {/* 3 */}

              <Slide direction="right">
                <div className="promo-banner">
                  <img
                    src={adbanner3}
                    alt=""
                  />

                  <div className="promo-content">
                    <h3>
                      Ice Cream
                    </h3>

                    <p>
                      Summer Specials
                    </p>
                  </div>
                </div>
              </Slide>
            </div>
          </section>

{/* ======================================================
              CATEGORIES
          ====================================================== */}

          <section className="category-section">
            <div className="section-head">
              <h2>
                Shop Popular Categories
              </h2>

              <Link to="/shop">
                View All
              </Link>
            </div>

            <div className="category-grid">
              {[
                {
                  img: dairybreadeggs,
                  name: "Dairy, Bread & Eggs",
                },

                {
                  img: fruitsvegetables,
                  name: "Fruits & Vegetables",
                },

                {
                  img: snackmunchies,
                  name: "Snack & Munchies",
                },

                {
                  img: bakerybiscuits,
                  name: "Bakery & Biscuits",
                },

                {
                  img: instantfood,
                  name: "Instant Food",
                },

                {
                  img: teacoffeedrinks,
                  name: "Tea & Coffee",
                },

                {
                  img: colddrinksjuices,
                  name: "Cold Drinks",
                },

                {
                  img: chickenmeatfish,
                  name: "Chicken & Fish",
                },

                {
                  img: babycare,
                  name: "Baby Care",
                },

                {
                  img: cleaningessentials,
                  name: "Cleaning",
                },

                {
                  img: petcare,
                  name: "Pet Care",
                },

                {
                  img: attaricedal,
                  name: "Atta Rice & Dal",
                },
              ].map((item, index) => (
                <Zoom key={index}>
                  <div
                    className="category-card"
                    onClick={() =>
                      navigate(
                        `/shop`
                      )
                    }
                  >
                    <img
                      src={item.img}
                      alt={item.name}
                    />

                    <p>{item.name}</p>
                  </div>
                </Zoom>
              ))}
            </div>
          </section>
          {/* ======================================================
              EVERYDAY ESSENTIALS
          ====================================================== */}
{/* ======================================================
    EVERYDAY ESSENTIALS
====================================================== */}

<div className="section-head">
  <h2>
    Everyday Essentials
  </h2>

  <Link to="/shop">
    View All
  </Link>
</div>

<div className="horizontal-products">
  {products.map((p) => (
    <div
      className="horizontal-item"
      key={p._id}
    >
      <ProductCard p={p} />
    </div>
  ))}
</div>

          
          {/* ======================================================
              BEST SELLING
          ====================================================== */}

          <div className="section-head">
            <h2>
              Best Selling
            </h2>

            <Link to="/shop">
              View All
            </Link>
          </div>

          <div className="horizontal-products">
            {bestSelling.map((p) => (
              <div
                className="horizontal-item"
                key={p._id}
              >
                <ProductCard p={p} />
              </div>
            ))}
          </div>

          {/* ======================================================
              HOT DEALS
          ====================================================== */}

          <div className="section-head">
            <h2>
              Hot Deals
            </h2>

            <Link to="/shop">
              View All
            </Link>
          </div>

          <div className="horizontal-products">
            {hotDeals.map((p) => (
              <div
                className="horizontal-item"
                key={p._id}
              >
                <ProductCard p={p} />
              </div>
            ))}
          </div>

          {/* ======================================================
              GROCERY BANNERS
          ====================================================== */}

          <section className="grocery-banners">
            <div className="grocery-grid">
              {/* 1 */}

              <Slide direction="left">
                <div
                  className="grocery-banner"
                  style={{
                    background: `url(${grocerybanner})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <h3>
                    Fruits & Vegetables
                  </h3>

                  <p>
                    Get Upto 30% OFF
                  </p>

                  <Link to="/shop">
                    Shop Now
                  </Link>
                </div>
              </Slide>

              {/* 2 */}

              <Slide direction="right">
                <div
                  className="grocery-banner"
                  style={{
                    background: `url(${grocerybanner2})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <h3>
                    Freshly Baked Buns
                  </h3>

                  <p>
                    Get Upto 25% OFF
                  </p>

                  <Link to="/shop">
                    Shop Now
                  </Link>
                </div>
              </Slide>
            </div>
          </section>

          {/* ======================================================
              APP DOWNLOAD SECTION
          ====================================================== */}

          <section className="app-section">
            <div
              className="app-wrapper"
              style={{
                background: `url(${map})`,
                backgroundSize: "cover",
              }}
            >
              {/* LEFT */}

              <div className="app-left">
                <h2>
                  Get The FreshCart App
                </h2>

                <p>
                  Download app and enjoy
                  instant grocery delivery.
                </p>

                <div className="app-buttons">
                  <img
                    src={appstore}
                    alt="appstore"
                  />

                  <img
                    src={googleplay}
                    alt="googleplay"
                  />
                </div>
              </div>

              {/* RIGHT */}

              <div className="app-right">
                <img
                  src={iphone}
                  alt="iphone"
                />
              </div>
            </div>
          </section>

          {/* ======================================================
              FEATURES
          ====================================================== */}

          <section className="feature-section">
            <div className="feature-grid">
              {/* 1 */}

              <Zoom>
                <div className="feature-card">
                  <img
                    src={refresh}
                    alt=""
                  />

                  <h3>
                    Easy Returns
                  </h3>

                  <p>
                    Return products easily.
                  </p>
                </div>
              </Zoom>

              {/* 2 */}

              <Zoom>
                <div className="feature-card">
                  <img
                    src={package1}
                    alt=""
                  />

                  <h3>
                    Wide Assortment
                  </h3>

                  <p>
                    5000+ products available.
                  </p>
                </div>
              </Zoom>

              {/* 3 */}

              <Zoom>
                <div className="feature-card">
                  <img
                    src={gift}
                    alt=""
                  />

                  <h3>
                    Best Offers
                  </h3>

                  <p>
                    Amazing discounts daily.
                  </p>
                </div>
              </Zoom>

              {/* 4 */}

              <Zoom>
                <div className="feature-card">
                  <img
                    src={clock}
                    alt=""
                  />

                  <h3>
                    10 Minute Delivery
                  </h3>

                  <p>
                    Grocery delivered fast.
                  </p>
                </div>
              </Zoom>
            </div>
          </section>

          {/* ======================================================
              BOTTOM CATEGORY SLIDER
          ====================================================== */}

          <div className="category-slider-wrap">
            <Slider {...categorySliderSettings}>
              {[
                {
                  img: product1,
                  name: "Baby Care",
                },

                {
                  img: product2,
                  name: "Atta Rice & Dal",
                },

                {
                  img: product3,
                  name: "Bakery",
                },

                {
                  img: product4,
                  name: "Chicken",
                },

                {
                  img: product5,
                  name: "Cleaning",
                },

                {
                  img: product6,
                  name: "Dairy",
                },

                {
                  img: product7,
                  name: "Instant Food",
                },

                {
                  img: product8,
                  name: "Pet Care",
                },

                {
                  img: product9,
                  name: "Snacks",
                },

                {
                  img: product10,
                  name: "Tea Coffee",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="slider-item"
                >
                  <div className="partner-list">
                    <img
                      src={item.img}
                      alt=""
                    />

                    <h6>{item.name}</h6>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          {/* ======================================================
              FLOATING CART
          ====================================================== */}

          {totalItems > 0 && (
            <div
              className="floating-cart"
              onClick={() =>
                navigate("/shopCart")
              }
            >
              <div className="cart-count">
                {totalItems}
              </div>

              <div className="cart-content">
                <div>
                  <h3>
                    View Cart
                  </h3>

                  <p>
                    ₹{totalPrice}
                  </p>
                </div>

                <div className="proceed-btn">
                  Proceed

                  <FiArrowRight />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Home;